//securityAccess:

import React, { useState } from 'react';
import { 
  FaShieldAlt, FaUserLock, FaHistory, FaLock, 
  FaDatabase, FaDownload, FaSync, FaSearch, 
  FaCheckCircle, FaTrashAlt, FaEdit, 
  FaTimes,
  FaSave
} from 'react-icons/fa';

import './SecurityAccess.css';


const SecurityModule = () => {
  const [activeTab, setActiveTab] = useState('audit');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  
  // --- RBAC State (Removed Users Assigned) ---
// --- RBAC State ---
  const [roles, setRoles] = useState([
    { 
      id: 1, name: 'Super Admin', permissions: 'Full Access', type: 'property', 
      access: { read: true, write: true, delete: true, export: true } 
    },
    { 
      id: 2, name: 'Staff', permissions: 'Read, Write', type: 'lease', 
      access: { read: true, write: true, delete: false, export: false } 
    },
    { 
      id: 3, name: 'Guest', permissions: 'Read Only', type: 'maintenance', 
      access: { read: true, write: false, delete: false, export: false } 
    },
  ]);

  // --- Audit Logs State & Search ---
  const [logSearch, setLogSearch] = useState('');
  const auditLogs = [
    { id: 1, user: 'Admin_User', action: 'Deleted Document', target: 'Deed_001.pdf', time: '2023-12-28 14:20', status: 'Success' },
    { id: 2, user: 'Staff_Member', action: 'Login Attempt', target: 'System', time: '2023-12-28 13:05', status: 'Success' },
    { id: 3, user: 'Unknown', action: 'Failed Login', target: 'Admin Portal', time: '2023-12-28 12:45', status: 'Failed' },
  ];

  const filteredLogs = auditLogs.filter(log => 
    log.user.toLowerCase().includes(logSearch.toLowerCase()) || 
    log.action.toLowerCase().includes(logSearch.toLowerCase())
  );

  // --- Modal Handlers ---
  const openEditModal = (role) => {
    setSelectedRole({ ...role }); // Clone to avoid direct mutation
    setIsModalOpen(true);
  };

  const togglePermission = (key) => {
    setSelectedRole({
      ...selectedRole,
      access: { ...selectedRole.access, [key]: !selectedRole.access[key] }
    });
  };

  const savePermissions = () => {
    // 1. Get all keys from the access object that are set to true
    const activePermissions = Object.keys(selectedRole.access)
      .filter(key => selectedRole.access[key]) // Only true values
      .map(key => key.charAt(0).toUpperCase() + key.slice(1)); // Capitalize (read -> Read)

    // 2. Determine the text for the "Permissions Scope" column
    let scopeText = "";
    if (activePermissions.length === 0) {
      scopeText = "No Access";
    } else if (activePermissions.length === Object.keys(selectedRole.access).length) {
      scopeText = "Full Access";
    } else {
      scopeText = activePermissions.join(", "); // e.g., "Read, Write"
    }

    // 3. Create the updated role object
    const updatedRole = { 
      ...selectedRole, 
      permissions: scopeText 
    };

    // 4. Update the state
    setRoles(roles.map(r => r.id === selectedRole.id ? updatedRole : r));
    setIsModalOpen(false);
  };

  // --- Handlers ---
  const handleBackup = () => alert("System backup initiated. Downloading archive...");
  
  const handleRestore = () => {
    if(window.confirm("Are you sure? This will revert the database to the last save point.")) {
      alert("System restored to Today, 04:00 AM version.");
    }
  };

  const deleteRole = (id) => {
    if(window.confirm("Delete this role? This action cannot be undone.")) {
      setRoles(roles.filter(r => r.id !== id));
    }
  };

  return (
    <div className="doc-wrapper">
      <div className="doc-container">
        
        {/* Header */}
        <header className="doc-header">
          <div className="header-text">
            <h1>Security & Access Control</h1>
            <p>Maintain system integrity and manage role-based security protocols.</p>
          </div>
          <div className="role-badge badge-admin">
            <FaShieldAlt /> ENCRYPTION: AES-256
          </div>
        </header>

        {/* Tab Navigation */}
        <div className="security-tabs">
          <button className={activeTab === 'audit' ? 'tab-btn active' : 'tab-btn'} onClick={() => setActiveTab('audit')}>
            <FaHistory /> Audit Logs
          </button>
          <button className={activeTab === 'data' ? 'tab-btn active' : 'tab-btn'} onClick={() => setActiveTab('data')}>
            <FaDatabase /> Data Safety
          </button>
        </div>

        {/* Main Content Card */}
        <div className="table-card">
          

          {/* TAB 2: AUDIT LOGS */}
          {activeTab === 'audit' && (
            <div className="security-content">
              <div className="doc-controls">
                <h3>Activity Logs</h3>
                <div className="search-box">
                  <FaSearch className="search-icon" />
                  <input 
                    type="text" 
                    placeholder="Search logs..." 
                    value={logSearch}
                    onChange={(e) => setLogSearch(e.target.value)}
                  />
                </div>
              </div>
              <div className="table-scroll-container">
                <table className="doc-table">
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Action</th>
                      <th>Target</th>
                      <th>Timestamp</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredLogs.map(log => (
                      <tr key={log.id}>
                        <td><strong>{log.user}</strong></td>
                        <td>{log.action}</td>
                        <td>{log.target}</td>
                        <td className="date-text">{log.time}</td>
                        <td>
                          <span className={log.status === 'Success' ? 'status-success' : 'status-failed'}>
                            {log.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* TAB 3: DATA SAFETY */}
          {activeTab === 'data' && (
            <div className="security-content data-grid">
              <div className="security-card">
                <div className="card-icon-circle secure">
                  <FaLock />
                </div>
                <h4>Data Encryption</h4>
                <p>Sensitive data is auto-encrypted. System integrity is checked daily.</p>
                <div className="status-indicator">
                  <FaCheckCircle color="#10b981" /> System Encrypted
                </div>
              </div>

              <div className="security-card">
                <div className="card-icon-circle">
                  <FaDatabase />
                </div>
                <h4>Cloud Backup</h4>
                <p>Last full backup created on <strong>Dec 29, 2025</strong></p>
                <div className="button-group-vertical">
                  <button className="upload-btn" onClick={handleBackup}>
                    <FaDownload /> Download Backup
                  </button>
                  <button className="btn-secondary" onClick={handleRestore}>
                    <FaSync /> Restore Database
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

       {/* --- PERMISSIONS EDIT MODAL --- */}
      {isModalOpen && selectedRole && (
        <div className="modal-overlay">
          <div className="modal-content permission-modal">
            <div className="modal-header">
              <h3>Edit Permissions: {selectedRole.name}</h3>
              <button className="close-btn" onClick={() => setIsModalOpen(false)}><FaTimes /></button>
            </div>
            <div className="modal-body">
              <p className="modal-subtitle">Select the access levels granted to this role:</p>
              
              <div className="permissions-grid">
                {Object.keys(selectedRole.access).map((key) => (
                  <div 
                    key={key} 
                    className={`permission-item ${selectedRole.access[key] ? 'enabled' : ''}`}
                    onClick={() => togglePermission(key)}
                  >
                    <div className="permission-info">
                      <span className="perm-title">{key.toUpperCase()}</span>
                      <span className="perm-desc">Allow user to {key} records</span>
                    </div>
                    <div className="perm-checkbox">
                      {selectedRole.access[key] && <FaCheckCircle />}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setIsModalOpen(false)}>Cancel</button>
              <button className="btn-primary" onClick={savePermissions}>
                <FaSave /> Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SecurityModule;